import React, {useCallback, useEffect, useRef, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import MicroreelsListUI from '../view/MicroreelsListUI';
import MicroreelsGroupView from '../view/MicroreelsGroupView';
import MicroreelsListStore from '../store/MicroreelsListStore';
import GenerateMicroreelStore from '../../../TapyblAI/GenerateMicroreel/store/GenerateMicroreelStore';
import {
  MicroreelGroup,
  MicroreelLesson,
  getGroupReels,
} from '../../../../data/models/Microreels/MicroreelGroup';
import {Lesson} from '../../../../data/models/Course/Lesson';
import {Course} from '../../../../data/models/Course/Course';
import {LessonPresentation} from '../../../../data/models/Course/LessonPresentation';
import ConfirmationModal from '../../../../uiToolkit/ConfirmationModal';
import EditMicroreelInfoModal from '../components/EditMicroreelInfoModal';
import MicroreelLearnersModal from '../components/MicroreelLearnersModal';
import {getMicroreelVideoSources} from '../microreelVideoSources';
import {buildChunkMap, readChunkMap, storeChunkMap} from '../microreelChunkMap';
import {getReelMeta, ReelGenerationMeta, ReelMeta} from '../reelMeta';
import {
  StoredGenerationInfo,
  clearGenerationInfo,
  readGenerationInfos,
} from '../../Generating/generatingTypes';
import {
  LessonContentMChoice,
  LessonContentMChoiceAnswer,
} from '../../../../data/models/LessonContent/LessonContentBase';
import ModalFade, {useModalFade} from '../components/ModalFade';
import MoveToProjectModal from '../../../Projects/components/MoveToProjectModal';
import {microreelsListText} from '../microreelsListText';
import {groupPosterText} from '../groupPosterText';
import {generatingMicroreelText} from '../../Generating/generatingMicroreelText';
import {toast} from 'react-toastify';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import NavigationStore from '../../../../navigation/store/NavigationStore';
import {APP_PERMISSIONS} from '../../../../data/permissions';
import {
  canUseLegacyVideoEditor,
  studioEditorPath,
} from '../../../../modules/MicroreelStudio/studioMedia';
import {studioPaths} from '../../../../modules/MicroreelStudio/paths';

interface Props {
  microreelsListStore?: MicroreelsListStore;
  generateMicroreelStore?: GenerateMicroreelStore;
  navigationStore?: NavigationStore;
}

const GENERATION_POLL_MS = 10000;

// Per-reel metadata fetches retry a few times before giving up (fresh
// generations can briefly 404 their content).
const META_MAX_ATTEMPTS = 3;
const META_RETRY_DELAY_MS = 4000;

const reelPosterText = {
  heading: microreelsListText.reelPosterHeading,
  replace: microreelsListText.posterReplace,
  remove: microreelsListText.posterRemove,
  emptyTitle: microreelsListText.posterEmptyTitle,
  emptyHint: microreelsListText.posterEmptyHint,
  hint: microreelsListText.reelPosterHint,
  hintEmpty: microreelsListText.reelPosterEmptyHint,
};

const MicroreelsListContainer = inject(
  'microreelsListStore',
  'generateMicroreelStore',
  'navigationStore',
)(
  observer((props: Props) => {
    const {microreelsListStore, generateMicroreelStore, navigationStore} =
      props;
    const [groups, setGroups] = useState<MicroreelGroup[]>([]);
    const [openedGroup, setOpenedGroup] = useState<MicroreelGroup>();
    const [reelToDeleteId, setReelToDeleteId] = useState<string>();
    const [groupToDeleteId, setGroupToDeleteId] = useState<string>();
    const [reelToEditId, setReelToEditId] = useState<string>();
    const [reelToEdit, setReelToEdit] = useState<Lesson>();
    const [groupToEditId, setGroupToEditId] = useState<string>();
    const [groupToEdit, setGroupToEdit] = useState<Course>();
    const [groupToMove, setGroupToMove] = useState<MicroreelGroup>();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pagesCount, setPagesCount] = useState(1);
    const [pendingGenerations, setPendingGenerations] = useState<
      StoredGenerationInfo[]
    >([]);
    const [selectedReel, setSelectedReel] = useState<MicroreelLesson>();
    const [publishedGroup, setPublishedGroup] = useState<Course>();
    const [learnersOpen, setLearnersOpen] = useState(false);
    const [reelPresentation, setReelPresentation] =
      useState<LessonPresentation>();
    // Reels of a group are consecutive windows into one generated file, so
    // the next presentation is fetched while the current one plays: the swap
    // then needs no request and the player seeks instead of reloading.
    const presentationCache = useRef<{[reelId: string]: LessonPresentation}>(
      {},
    );
    // Bumped to refetch the selected reel's presentation after an edit.
    const [presentationVersion, setPresentationVersion] = useState(0);
    const [reelMetas, setReelMetas] = useState<{[reelId: string]: ReelMeta}>(
      {},
    );
    const [reelAckFlags, setReelAckFlags] = useState<{
      [reelId: string]: boolean;
    }>({});
    const [reelGenMeta, setReelGenMeta] = useState<{
      [reelId: string]: ReelGenerationMeta;
    }>({});
    // Whether each source file is editable in the video editor (only
    // AI-generated videos are; uploaded ones make the editor fail with
    // invalid_operation). undefined = not fetched yet → button enabled.
    const [studioJobByFile, setStudioJobByFile] = useState<{
      [fileId: string]: string;
    }>({});
    const [videoEditableByFile, setVideoEditableByFile] = useState<{
      [fileId: string]: boolean;
    }>({});
    const requestedMetaIds = useRef(new Set<string>());
    const metaAttempts = useRef<{[reelId: string]: number}>({});
    const metaRetryTimer = useRef<number>();
    const [metaRetryTick, setMetaRetryTick] = useState(0);
    const [questionReel, setQuestionReel] = useState<MicroreelLesson>();
    const [questionNode, setQuestionNode] = useState<LessonContentMChoice>();
    // Covers the whole save-question span (draft save + republish): the
    // editor stays blocked until the published copy is up to date.
    const [questionSaving, setQuestionSaving] = useState(false);
    const [cancelGenerationOpen, setCancelGenerationOpen] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const {organizationId, groupId} = useParams();

    useEffect(() => {
      fetchGroups();
    }, [search]);

    useEffect(() => {
      const openGroupId = (location.state as {openGroupId?: string} | null)
        ?.openGroupId;
      if (!openGroupId || !organizationId) return;
      navigate(`/manage/${organizationId}/microreels/${openGroupId}`, {
        replace: true,
        state: null,
      });
    }, [location.state, organizationId]);

    // The opened group lives in the URL so a page reload restores it. The
    // group payload only comes from the paginated list, so keep loading pages
    // until the group shows up (or bail back to the list).
    useEffect(() => {
      if (!groupId) {
        setOpenedGroup(undefined);
        setSelectedReel(undefined);
        setReelPresentation(undefined);
        setQuestionReel(undefined);
        setQuestionNode(undefined);
        return;
      }
      if (openedGroup?.id === groupId) return;
      const group = groups.find(item => item.id === groupId);
      if (group) {
        setOpenedGroup(group);
        return;
      }
      if (microreelsListStore!.isLoading) return;
      if (page + 1 <= pagesCount) {
        loadNextPage();
      } else if (organizationId) {
        navigate(`/manage/${organizationId}/microreels`, {replace: true});
      }
    }, [groupId, groups, page, pagesCount, openedGroup?.id]);

    useEffect(() => {
      if (!organizationId || !reelToEditId) {
        setReelToEdit(undefined);
        return;
      }
      microreelsListStore!
        .getReel(organizationId, reelToEditId)
        .then(lesson => {
          if (lesson) {
            setReelToEdit(lesson);
          } else {
            setReelToEditId(undefined);
          }
        });
    }, [reelToEditId, organizationId]);

    useEffect(() => {
      if (!organizationId || !groupToEditId) {
        setGroupToEdit(undefined);
        return;
      }
      microreelsListStore!
        .getGroup(organizationId, groupToEditId)
        .then(course => {
          if (course) {
            setGroupToEdit(course);
          } else {
            setGroupToEditId(undefined);
          }
        });
    }, [groupToEditId, organizationId]);

    useEffect(() => {
      if (!openedGroup) return;
      const reels = getGroupReels(openedGroup);
      const stillThere =
        selectedReel && reels.some(reel => reel.id === selectedReel.id);
      if (!stillThere) {
        setSelectedReel(reels[0]);
      }
    }, [openedGroup]);

    useEffect(() => {
      if (!organizationId || !openedGroup) {
        setPublishedGroup(undefined);
        return;
      }
      let active = true;
      microreelsListStore!
        .getPublishedGroup(organizationId, openedGroup.id)
        .then(published => {
          if (active) {
            setPublishedGroup(published);
          }
        });
      return () => {
        active = false;
      };
    }, [organizationId, openedGroup?.id]);

    useEffect(() => {
      presentationCache.current = {};
    }, [openedGroup?.id]);

    useEffect(() => {
      if (!organizationId || !openedGroup || !selectedReel) {
        setReelPresentation(undefined);
        return;
      }
      const cached = presentationCache.current[selectedReel.id];
      if (cached) {
        setReelPresentation(cached);
        return;
      }
      let active = true;
      setReelPresentation(undefined);
      microreelsListStore!
        .getReelPreview(organizationId, openedGroup.id, selectedReel.id)
        .then(presentation => {
          if (presentation) {
            presentationCache.current[selectedReel.id] = presentation;
          }
          if (active) {
            setReelPresentation(presentation);
          }
        });
      return () => {
        active = false;
      };
    }, [
      organizationId,
      openedGroup?.id,
      selectedReel?.id,
      presentationVersion,
    ]);

    useEffect(() => {
      if (!organizationId || !openedGroup || !selectedReel) return;
      const groupReels = getGroupReels(openedGroup);
      const next =
        groupReels[
          groupReels.findIndex(reel => reel.id === selectedReel.id) + 1
        ];
      if (!next || presentationCache.current[next.id]) return;
      let active = true;
      microreelsListStore!
        .getReelPreviewSilent(organizationId, openedGroup.id, next.id)
        .then(presentation => {
          if (active && presentation) {
            presentationCache.current[next.id] = presentation;
          }
        });
      return () => {
        active = false;
      };
    }, [organizationId, openedGroup?.id, selectedReel?.id]);

    useEffect(() => {
      if (!organizationId || !openedGroup) return;
      let active = true;
      getGroupReels(openedGroup).forEach(reel => {
        if (requestedMetaIds.current.has(reel.id)) return;
        if ((metaAttempts.current[reel.id] ?? 0) >= META_MAX_ATTEMPTS) return;
        requestedMetaIds.current.add(reel.id);
        metaAttempts.current[reel.id] =
          (metaAttempts.current[reel.id] ?? 0) + 1;
        microreelsListStore!
          .getReelContentSilent(organizationId, reel.id)
          .then(lessonContent => {
            if (!lessonContent) {
              // A failed fetch (fresh generation, flaky network…) used to
              // leave the row without its duration forever: schedule a
              // bounded retry pass instead.
              requestedMetaIds.current.delete(reel.id);
              if (
                active &&
                (metaAttempts.current[reel.id] ?? 0) < META_MAX_ATTEMPTS &&
                metaRetryTimer.current === undefined
              ) {
                metaRetryTimer.current = window.setTimeout(() => {
                  metaRetryTimer.current = undefined;
                  setMetaRetryTick(tick => tick + 1);
                }, META_RETRY_DELAY_MS);
              }
              return;
            }
            if (active) {
              setReelMetas(prev => ({
                ...prev,
                [reel.id]: getReelMeta(lessonContent),
              }));
            }
          });
      });
      return () => {
        active = false;
      };
    }, [organizationId, openedGroup?.id, metaRetryTick]);

    useEffect(
      () => () => {
        if (metaRetryTimer.current !== undefined) {
          window.clearTimeout(metaRetryTimer.current);
        }
      },
      [],
    );

    useEffect(() => {
      if (!organizationId || !openedGroup) return;
      let active = true;
      const fileIds = Array.from(
        new Set(
          getGroupReels(openedGroup).map(
            reel => reel.libraryMediaFileId || reel.id,
          ),
        ),
      ).filter(fileId => videoEditableByFile[fileId] === undefined);
      fileIds.forEach(fileId => {
        microreelsListStore!
          .getMediaFileSilent(organizationId, fileId)
          .then(file => {
            if (!active || !file) return;
            if (file.studioJobId)
              setStudioJobByFile(prev => ({
                ...prev,
                [fileId]: file.studioJobId!,
              }));
            setVideoEditableByFile(prev => ({
              ...prev,
              [fileId]: canUseLegacyVideoEditor(file),
            }));
          });
      });
      return () => {
        active = false;
      };
    }, [organizationId, openedGroup?.id]);

    // The attestation flag and the generation language/dialect/gender live on
    // the Lesson entity, only exposed by the folder lessons endpoint — the
    // learner preview and the Details grid need them per reel.
    useEffect(() => {
      if (!organizationId || !openedGroup) return;
      let active = true;
      (openedGroup.folders ?? []).forEach(folder => {
        microreelsListStore!
          .getFolderLessonsSilent(organizationId, folder.id)
          .then(lessons => {
            if (!active) return;
            setReelAckFlags(prev => {
              const next = {...prev};
              lessons.forEach(lesson => {
                next[lesson.id] = !!lesson.addAckNode;
              });
              return next;
            });
            setReelGenMeta(prev => {
              const next = {...prev};
              lessons.forEach(lesson => {
                next[lesson.id] = {
                  language: lesson.language ?? undefined,
                  dialect: lesson.dialect ?? undefined,
                  gender: lesson.gender ?? undefined,
                };
              });
              return next;
            });
          });
      });
      return () => {
        active = false;
      };
    }, [organizationId, openedGroup?.id]);

    // available_generation no longer reflects in-flight jobs; generations
    // are tracked in localStorage.
    useEffect(() => {
      if (!organizationId) return;
      let active = true;
      let intervalId: ReturnType<typeof setInterval> | null = null;

      const stopPolling = () => {
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };

      const check = async () => {
        const stored = readGenerationInfos(organizationId);
        setPendingGenerations(stored);
        if (!stored.length) {
          stopPolling();
          return;
        }
        const result =
          await generateMicroreelStore!.settleGenerations(organizationId);
        if (!active) return;
        if (result.failed.length) {
          toast.error(generatingMicroreelText.generationFailed, toastConfig);
        }
        if (result.finished.length) {
          microreelsListStore!
            .getMicroreels(organizationId, 0, search)
            .then(res => {
              if (!active) return;
              setGroups(res.data);
              setPagesCount(res.pagesCount);
              setPage(0);
            });
        }
        setPendingGenerations(result.pending);
        if (result.pending.length) {
          if (intervalId === null) {
            intervalId = setInterval(check, GENERATION_POLL_MS);
          }
        } else {
          stopPolling();
        }
      };

      check();
      return () => {
        active = false;
        stopPolling();
      };
    }, [organizationId, search]);

    const fetchGroups = () => {
      if (organizationId) {
        setPage(0);
        microreelsListStore!
          .getMicroreels(organizationId, 0, search)
          .then(res => {
            setGroups(res.data);
            setPagesCount(res.pagesCount);
            // Posters come back as fresh signed urls, so the opened group has
            // to be taken from the server rather than patched in place.
            setOpenedGroup(current =>
              current
                ? res.data.find(group => group.id === current.id) ?? current
                : current,
            );
          });
      }
    };

    const onOpenGroup = (group: MicroreelGroup) => {
      setOpenedGroup(group);
      navigate(`/manage/${organizationId}/microreels/${group.id}`);
    };

    const onCreate = () => {
      navigate(`/manage/${organizationId}/home/generate_microreel`);
    };

    const backPath = (location.state as {backPath?: string} | null)?.backPath;
    const backToProject = !!backPath?.startsWith(
      `/manage/${organizationId}/projects/`,
    );

    const onCloseGroup = () => {
      navigate(backPath ?? `/manage/${organizationId}/microreels`);
    };

    const onGroupAnalytics = (group: MicroreelGroup, backToGroup?: boolean) => {
      navigate(
        `/manage/${organizationId}/analytics/course/${group.publishedCourseId}/learners`,
        {
          state: {
            backPath: `/manage/${organizationId}/microreels${
              backToGroup ? `/${group.id}` : ''
            }`,
          },
        },
      );
    };

    const onGroupCourseEditor = (group: MicroreelGroup) => {
      const folderId = group.folders?.[0]?.id;
      navigate(
        `/editor/${organizationId}/courses/${group.id}/folders/${folderId}/lessons`,
      );
    };

    const isReelVideoEditable = (reel: MicroreelLesson) =>
      videoEditableByFile[reel.libraryMediaFileId || reel.id] !== false;

    // The chunk→reel map lets the editor lock by stable chunk ids instead of
    // re-deriving the mapping from time windows (which drift after edits).
    // Built once per source video, while the timings still line up.
    const ensureChunkMap = async (mediaFileId: string) => {
      if (!organizationId || !openedGroup) return;
      if (readChunkMap(mediaFileId)) return;
      const chunks = await microreelsListStore!.getVideoChunksSilent(
        organizationId,
        mediaFileId,
      );
      if (!chunks?.length) return;
      const reels = getGroupReels(openedGroup).filter(
        r => (r.libraryMediaFileId || r.id) === mediaFileId,
      );
      const contents = await Promise.all(
        reels.map(r =>
          microreelsListStore!.getReelContentSilent(organizationId, r.id),
        ),
      );
      const map = buildChunkMap(
        chunks,
        reels.map((r, index) => ({
          reelId: r.id,
          sources: getMicroreelVideoSources(contents[index]),
        })),
      );
      if (map) {
        storeChunkMap(mediaFileId, map);
      }
    };

    const onEditVideoReel = async (reel: MicroreelLesson) => {
      if (!organizationId || !openedGroup) return;
      const file = await microreelsListStore!.getMediaFileSilent(
        organizationId,
        reel.libraryMediaFileId || reel.id,
      );
      if (!file) {
        toast.error('Unable to load the video. Please try again.', toastConfig);
        return;
      }
      const studioJobId =
        file.studioJobId || studioJobByFile[reel.libraryMediaFileId || reel.id];
      if (studioJobId) {
        navigate(studioEditorPath(organizationId, studioJobId));
        return;
      }
      if (!canUseLegacyVideoEditor(file)) {
        const folderId =
          openedGroup.folders?.find(
            folder => folder.lessons?.some(lesson => lesson.id === reel.id),
          )?.id ?? openedGroup.folders?.[0]?.id;
        navigate(
          `/editor/${organizationId}/courses/${openedGroup.id}/folders/${folderId}/lessons/${reel.id}/edit`,
        );
        return;
      }
      const mediaFileId = reel.libraryMediaFileId || reel.id;
      const state: {
        fromMicroreels: boolean;
        microreelGroupId: string;
        scopeReelId: string;
        scopeTitle?: string;
        scopeUrls?: string[];
        scopeStart?: number;
        scopeEnd?: number;
      } = {
        fromMicroreels: true,
        microreelGroupId: openedGroup.id,
        scopeReelId: reel.id,
      };
      await ensureChunkMap(mediaFileId);
      const presentation = await microreelsListStore!.getReelPreview(
        organizationId,
        openedGroup.id,
        reel.id,
      );
      const sources = getMicroreelVideoSources(presentation?.lessonContent);
      if (sources.length) {
        const stripQuery = (url: string) => url.split('?')[0];
        state.scopeTitle = reel.title;
        state.scopeUrls = Array.from(
          new Set(sources.map(source => stripQuery(source.url))),
        );
        state.scopeStart = Math.min(
          ...sources.map(source => source.startInSeconds),
        );
        state.scopeEnd = Math.max(
          ...sources.map(source => source.endInSeconds),
        );
      }
      navigate(`/manage/${organizationId}/videoEditor/${mediaFileId}`, {
        state,
      });
    };

    // The Studio job that rendered the group, if any. Every reel of a Studio
    // group comes from that job's one rendered file.
    const findStudioJob = async (group: MicroreelGroup) => {
      const reel = getGroupReels(group)[0];
      if (!organizationId || !reel) return undefined;
      const fileId = reel.libraryMediaFileId || reel.id;
      if (studioJobByFile[fileId]) return studioJobByFile[fileId];
      // Already looked up when the group opened, and not a Studio render.
      if (videoEditableByFile[fileId] !== undefined) return undefined;
      const file = await microreelsListStore!.getMediaFileSilent(
        organizationId,
        fileId,
      );
      return file?.studioJobId || undefined;
    };

    const onGroupLearners = async () => {
      if (!organizationId || !openedGroup) return;
      // The learners modal needs a published course; publish silently first.
      if (!publishedGroup) {
        // Studio publishes its own groups under the ids its revisions use (the
        // platform publish refuses them), so an unshared Studio draft is
        // shared from Studio's review page.
        const studioJobId = await findStudioJob(openedGroup);
        if (studioJobId) {
          navigate(studioPaths(organizationId).job(studioJobId, 'review'));
          return;
        }
        const published = await onPublishGroup();
        if (!published) return;
      }
      setLearnersOpen(true);
    };

    // cancel_generation stops every running microreel generation of the org,
    // so all tracked generations are dropped, not just the most recent one.
    const onConfirmCancelGeneration = async () => {
      if (!organizationId) return;
      const success =
        await microreelsListStore!.cancelGeneration(organizationId);
      setCancelGenerationOpen(false);
      if (success) {
        clearGenerationInfo(organizationId);
        setPendingGenerations(readGenerationInfos(organizationId));
        fetchGroups();
      }
    };

    const onEditQuestion = async (reel: MicroreelLesson) => {
      if (!organizationId) return;
      const file = await microreelsListStore!.getMediaFileSilent(
        organizationId,
        reel.libraryMediaFileId || reel.id,
      );
      if (!file) {
        toast.error('Unable to load the video. Please try again.', toastConfig);
        return;
      }
      if (file.studioJobId) {
        navigate(studioEditorPath(organizationId, file.studioJobId));
        return;
      }
      if (file.studioRevisionId) {
        toast.info(
          'Edit this knowledge check in Microreel Studio.',
          toastConfig,
        );
        return;
      }
      const node = await microreelsListStore!.getReelQuestion(
        organizationId,
        reel.id,
      );
      if (!node) {
        toast.info(microreelsListText.noQuestionToEdit, toastConfig);
        return;
      }
      setQuestionReel(reel);
      setQuestionNode(node);
    };

    const onCloseQuestion = () => {
      setQuestionReel(undefined);
      setQuestionNode(undefined);
    };

    const onSaveQuestion = async (
      question: string,
      answers: LessonContentMChoiceAnswer[],
    ) => {
      if (!organizationId || !questionReel || !questionNode || questionSaving)
        return;
      setQuestionSaving(true);
      try {
        const success = await microreelsListStore!.saveReelQuestion(
          organizationId,
          questionReel.id,
          {...questionNode, question, answers},
        );
        if (!success) return;
        delete presentationCache.current[questionReel.id];
        // The Preview reads the selected reel's presentation, which would
        // otherwise keep the question as it was before the edit.
        if (questionReel.id === selectedReel?.id) {
          setPresentationVersion(version => version + 1);
        }
        // Question edits touch the draft content only; learners and
        // copy-to-course read the published version. Republish right here,
        // keeping the editor blocked until it lands — with the debounced
        // background republish the edit looked like it never applied.
        // Republish unconditionally instead of gating on
        // openedGroup.published: a real draft answers entity_not_found,
        // which ignoreNotPublished treats as done.
        if (openedGroup) {
          pendingRepublishIds.current.delete(openedGroup.id);
          const republished = await microreelsListStore!.republishGroupSilent(
            organizationId,
            openedGroup.id,
            {ignoreNotPublished: true},
          );
          // On failure the editor stays open (the error toast is already
          // shown); saving again retries the republish.
          if (!republished) return;
        }
        onCloseQuestion();
      } finally {
        setQuestionSaving(false);
      }
    };

    const onMoveReel = (reel: MicroreelLesson, direction: 1 | -1) => {
      if (!organizationId || !openedGroup) return;
      const folder = openedGroup.folders.find(f =>
        (f.lessons ?? []).some(lesson => lesson.id === reel.id),
      );
      if (!folder) return;
      const lessons = [...(folder.lessons ?? [])].sort(
        (x, y) => x.order - y.order,
      );
      const index = lessons.findIndex(lesson => lesson.id === reel.id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= lessons.length) return;
      [lessons[index], lessons[target]] = [lessons[target], lessons[index]];
      const reordered = lessons.map((lesson, order) => ({...lesson, order}));
      const updatedGroup = {
        ...openedGroup,
        folders: openedGroup.folders.map(f =>
          f.id === folder.id ? {...f, lessons: reordered} : f,
        ),
      };
      setOpenedGroup(updatedGroup);
      // Mirror the new order into the groups list: reopening the group finds
      // it there (the container stays mounted between the list and the group,
      // so there is no refetch on the way back).
      setGroups(current =>
        current.map(group =>
          group.id === updatedGroup.id ? updatedGroup : group,
        ),
      );
      microreelsListStore!.reorderReels(
        organizationId,
        folder.id,
        reordered.map(lesson => ({id: lesson.id, order: lesson.order})),
      );
      // Reordering also only touches the draft; several quick moves collapse
      // into a single debounced republish.
      scheduleRepublish(openedGroup.id);
    };

    const onAddToCourse = () => {
      navigate(`/manage/${organizationId}/courses`);
    };

    const onPublishGroup = async () => {
      if (!organizationId || !openedGroup) return false;
      const success = await microreelsListStore!.publishGroup(
        organizationId,
        openedGroup.id,
        !!publishedGroup,
      );
      if (!success) return false;
      // A manual (re)publish already covers any pending debounced one.
      pendingRepublishIds.current.delete(openedGroup.id);
      const published = await microreelsListStore!.getPublishedGroup(
        organizationId,
        openedGroup.id,
      );
      setPublishedGroup(published);
      const res = await microreelsListStore!.getMicroreels(
        organizationId,
        0,
        search,
      );
      setGroups(res.data);
      setPagesCount(res.pagesCount);
      setPage(0);
      const refreshed = res.data.find(group => group.id === openedGroup.id);
      if (refreshed) {
        setOpenedGroup(refreshed);
      }
      return true;
    };

    // Title/description edits need a republish so the published copy (used
    // by copy-to-course and the learner feed) picks them up, but editing
    // several names in a row must not republish once per edit: edits are
    // collected and a single republish fires after a quiet period (or when
    // leaving the page).
    const REPUBLISH_DEBOUNCE_MS = 5000;
    const pendingRepublishIds = useRef(new Set<string>());
    const inFlightRepublishIds = useRef(new Set<string>());
    const republishTimer = useRef<number>();

    const flushPendingRepublish = useCallback(() => {
      if (republishTimer.current) {
        window.clearTimeout(republishTimer.current);
        republishTimer.current = undefined;
      }
      if (!organizationId) return;
      const ids = Array.from(pendingRepublishIds.current);
      pendingRepublishIds.current.clear();
      ids.forEach(id => {
        inFlightRepublishIds.current.add(id);
        // ignoreNotPublished: edits schedule a republish unconditionally, so
        // draft groups resolve here as a silent no-op (entity_not_found).
        microreelsListStore!
          .republishGroupSilent(organizationId, id, {
            ignoreNotPublished: true,
          })
          .finally(() => inFlightRepublishIds.current.delete(id));
      });
    }, [organizationId]);

    // Closing or reloading the tab while a republish is debounced or still in
    // flight would leave the published copy stale: warn first, and if the
    // user leaves anyway re-send those republishes as keepalive requests,
    // which survive the unload. SPA navigation is not affected (the flush on
    // unmount completes normally with the app alive).
    useEffect(() => {
      const hasUnfinishedRepublish = () =>
        pendingRepublishIds.current.size > 0 ||
        inFlightRepublishIds.current.size > 0;

      const onBeforeUnload = (ev: BeforeUnloadEvent) => {
        if (!hasUnfinishedRepublish()) return;
        ev.preventDefault();
        ev.returnValue = '';
      };

      const onPageHide = () => {
        if (!organizationId || !hasUnfinishedRepublish()) return;
        const ids = new Set(
          Array.from(pendingRepublishIds.current).concat(
            Array.from(inFlightRepublishIds.current),
          ),
        );
        pendingRepublishIds.current.clear();
        ids.forEach(id =>
          microreelsListStore!.republishGroupKeepalive(organizationId, id),
        );
      };

      window.addEventListener('beforeunload', onBeforeUnload);
      window.addEventListener('pagehide', onPageHide);
      return () => {
        window.removeEventListener('beforeunload', onBeforeUnload);
        window.removeEventListener('pagehide', onPageHide);
      };
    }, [organizationId]);

    const scheduleRepublish = (groupId: string) => {
      pendingRepublishIds.current.add(groupId);
      if (republishTimer.current) {
        window.clearTimeout(republishTimer.current);
      }
      republishTimer.current = window.setTimeout(
        flushPendingRepublish,
        REPUBLISH_DEBOUNCE_MS,
      );
    };

    useEffect(() => flushPendingRepublish, [flushPendingRepublish]);

    const onEditReel = (reelId: string) => {
      setReelToEditId(reelId);
    };

    const onCancelEditReel = () => {
      setReelToEditId(undefined);
      setReelToEdit(undefined);
    };

    const onSaveReel = (name: string, description: string, cover?: File) => {
      if (!organizationId || !reelToEdit) return;
      const updated: Lesson = {
        ...reelToEdit,
        title: name,
        description,
        newImage: cover,
      };
      microreelsListStore!.editReel(organizationId, updated).then(success => {
        if (success) {
          if (openedGroup) {
            const folders = openedGroup.folders.map(folder => ({
              ...folder,
              lessons: (folder.lessons ?? []).map(reel =>
                reel.id === updated.id
                  ? {...reel, title: name, description}
                  : reel,
              ),
            }));
            setOpenedGroup({...openedGroup, folders});
            scheduleRepublish(openedGroup.id);
          }
          fetchGroups();
          onCancelEditReel();
        }
      });
    };

    const onEditGroup = (group: MicroreelGroup) => {
      setGroupToEditId(group.id);
    };

    const onCancelEditGroup = () => {
      setGroupToEditId(undefined);
      setGroupToEdit(undefined);
    };

    const onSaveGroup = (name: string, description: string, cover?: File) => {
      if (!organizationId || !groupToEdit) return;
      const orgId = organizationId;
      const updated: Course = {...groupToEdit, title: name, description};
      microreelsListStore!.editGroup(orgId, updated, cover).then(success => {
        if (success) {
          if (openedGroup && openedGroup.id === updated.id) {
            setOpenedGroup({...openedGroup, title: name});
          }
          if (updated.id) {
            scheduleRepublish(updated.id);
          }
          fetchGroups();
          onCancelEditGroup();
        }
      });
    };

    const onDeleteReel = (reelId: string) => {
      setReelToDeleteId(reelId);
    };

    const onConfirmDeleteReel = () => {
      if (organizationId && reelToDeleteId) {
        microreelsListStore!
          .deleteReel(organizationId, reelToDeleteId)
          .then(success => {
            if (success && openedGroup) {
              // Deleting only touches the draft: without this the reel stays
              // in the published copy and learners keep seeing it.
              scheduleRepublish(openedGroup.id);
              const folders = openedGroup.folders.map(folder => ({
                ...folder,
                lessons: (folder.lessons ?? []).filter(
                  lesson => lesson.id !== reelToDeleteId,
                ),
              }));
              const remaining = folders.flatMap(folder => folder.lessons);
              if (remaining.length) {
                setOpenedGroup({...openedGroup, folders});
              } else {
                navigate(`/manage/${organizationId}/microreels`);
              }
            }
            if (success) {
              fetchGroups();
            }
            setReelToDeleteId(undefined);
          });
      }
    };

    const onDeleteGroup = (group: MicroreelGroup) => {
      setGroupToDeleteId(group.id);
    };

    const onConfirmDeleteGroup = () => {
      if (organizationId && groupToDeleteId) {
        microreelsListStore!
          .deleteGroup(organizationId, groupToDeleteId)
          .then(success => {
            if (success) {
              if (openedGroup?.id === groupToDeleteId) {
                navigate(`/manage/${organizationId}/microreels`);
              }
              fetchGroups();
            }
            setGroupToDeleteId(undefined);
          });
      }
    };

    const loadNextPage = useCallback(async () => {
      if (organizationId && page + 1 <= pagesCount) {
        const result = await microreelsListStore!.getMicroreels(
          organizationId,
          page + 1,
          search,
        );
        setPagesCount(result.pagesCount);
        setGroups(oldGroups => [...oldGroups, ...result.data]);
        setPage(page + 1);
      }
    }, [pagesCount, page]);

    const isLastReel = getGroupReels(openedGroup).length === 1;

    const cancelGenerationFade = useModalFade(cancelGenerationOpen);
    const deleteReelFade = useModalFade(reelToDeleteId !== undefined);
    const deleteGroupFade = useModalFade(groupToDeleteId !== undefined);
    const editReelFade = useModalFade(reelToEditId !== undefined);
    const editGroupFade = useModalFade(groupToEditId !== undefined);
    const learnersFade = useModalFade(
      learnersOpen && !!openedGroup && !!publishedGroup,
    );

    return (
      <>
        {openedGroup ? (
          <MicroreelsGroupView
            group={openedGroup}
            selectedReel={selectedReel}
            presentation={reelPresentation}
            reelMetas={reelMetas}
            reelAckFlags={reelAckFlags}
            reelGenMeta={reelGenMeta}
            isPreviewLoading={microreelsListStore!.isPreviewLoading}
            onSelectReel={setSelectedReel}
            onBack={onCloseGroup}
            backLabel={
              backToProject ? openedGroup.projectName ?? undefined : undefined
            }
            onLearners={onGroupLearners}
            onAnalytics={() => onGroupAnalytics(openedGroup, true)}
            onEditGroupInfo={() => onEditGroup(openedGroup)}
            onAddToCourse={onAddToCourse}
            onMoveReel={onMoveReel}
            isPublishing={microreelsListStore!.isPublishing}
            onEditVideo={onEditVideoReel}
            isReelVideoEditable={isReelVideoEditable}
            onEditInfo={onEditReel}
            onEditQuestion={onEditQuestion}
            onDelete={onDeleteReel}
            questionEditReel={questionReel}
            questionEditNode={questionNode}
            isQuestionSaving={questionSaving}
            onSaveQuestion={onSaveQuestion}
            onCloseQuestion={onCloseQuestion}
          />
        ) : (
          <MicroreelsListUI
            canCreate={
              navigationStore?.userPermissions.includes(
                APP_PERMISSIONS.ORG_EDIT_COURSES,
              ) === true
            }
            groups={groups}
            onOpenGroup={onOpenGroup}
            onCourseEditor={onGroupCourseEditor}
            onEditInfo={onEditGroup}
            onDelete={onDeleteGroup}
            onLearnerProgress={onGroupAnalytics}
            onMoveToProject={setGroupToMove}
            onCreate={onCreate}
            loadNextPage={loadNextPage}
            isLoading={microreelsListStore!.isLoading}
            pendingGenerations={pendingGenerations}
            onOpenGenerating={info =>
              navigate(`/manage/${organizationId}/microreels/generating`, {
                state: info,
              })
            }
            onCancelGeneration={() => setCancelGenerationOpen(true)}
            search={search}
            setSearch={setSearch}
          />
        )}
        {cancelGenerationFade.rendered && (
          <ModalFade closing={cancelGenerationFade.closing}>
            <ConfirmationModal
              shown
              onAccept={onConfirmCancelGeneration}
              onCancel={() => setCancelGenerationOpen(false)}
              title={microreelsListText.cancelGenerationConfirm}
              acceptText={microreelsListText.cancelGeneration}
            />
          </ModalFade>
        )}
        {deleteReelFade.rendered && (
          <ModalFade closing={deleteReelFade.closing}>
            <ConfirmationModal
              shown
              onAccept={onConfirmDeleteReel}
              onCancel={() => setReelToDeleteId(undefined)}
              title={
                isLastReel
                  ? microreelsListText.deleteLastReel
                  : microreelsListText.deleteReel
              }
              acceptText={microreelsListText.delete}
            />
          </ModalFade>
        )}
        {deleteGroupFade.rendered && (
          <ModalFade closing={deleteGroupFade.closing}>
            <ConfirmationModal
              shown
              onAccept={onConfirmDeleteGroup}
              onCancel={() => setGroupToDeleteId(undefined)}
              title={microreelsListText.deleteGroup}
              acceptText={microreelsListText.delete}
            />
          </ModalFade>
        )}
        {editReelFade.rendered && (
          <ModalFade closing={editReelFade.closing}>
            <EditMicroreelInfoModal
              shown
              headerTitle={microreelsListText.editInfoTitle}
              initialName={reelToEdit?.title}
              initialDescription={reelToEdit?.description}
              posterText={reelPosterText}
              posterAspectRatio="9 / 16"
              posterMaxWidth={190}
              initialCover={reelToEdit?.poster}
              isLoading={microreelsListStore!.isEditLoading}
              onCancel={onCancelEditReel}
              onSave={onSaveReel}
            />
          </ModalFade>
        )}
        {editGroupFade.rendered && (
          <ModalFade closing={editGroupFade.closing}>
            <EditMicroreelInfoModal
              shown
              headerTitle={microreelsListText.editGroupInfoTitle}
              initialName={groupToEdit?.title}
              initialDescription={groupToEdit?.description}
              posterText={groupPosterText}
              posterAspectRatio="16 / 9"
              initialCover={groupToEdit?.image}
              isLoading={microreelsListStore!.isEditLoading}
              onCancel={onCancelEditGroup}
              onSave={onSaveGroup}
            />
          </ModalFade>
        )}
        {groupToMove && (
          <MoveToProjectModal
            courseId={groupToMove.id}
            currentProjectId={groupToMove.projectId}
            onClose={() => setGroupToMove(undefined)}
            onMoved={() => {
              setGroupToMove(undefined);
              fetchGroups();
            }}
          />
        )}
        {learnersFade.rendered && openedGroup && publishedGroup && (
          <ModalFade closing={learnersFade.closing}>
            <MicroreelLearnersModal
              groupTitle={openedGroup.title}
              publishedCourseId={publishedGroup.id || ''}
              onClose={() => setLearnersOpen(false)}
            />
          </ModalFade>
        )}
      </>
    );
  }),
);

export default MicroreelsListContainer;
